/**
 * Phase 39 — "Right now" under a card on a tracker source: one line per item
 * the card would judge today (a group's list of the day, a pool route) with
 * its numbers over the range and whether the rule as typed holds on them —
 * the engine's own judge, live, as the levers move. Quiet until there is
 * something to say.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Activity } from 'lucide-react';
import type { DropGenSourcePreviewItem } from '@trafficpulse/shared';
import { FLOW_METRIC_LABELS } from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

import { formatMetric } from '../../general/hour/formula/hour-metric-catalog';
import { type SourcePreviewArgs, useSourcePreview } from './use-source-preview';

const VERDICT_WORDS = {
  FAIL: 'holds',
  PASS: 'does not hold',
  INCONCLUSIVE: 'unknown',
  PENDING: 'pending',
} as const;
const VERDICT_TONE = {
  FAIL: 'text-primary',
  PASS: 'text-muted-foreground',
  INCONCLUSIVE: 'text-warning',
  PENDING: 'text-muted-foreground',
} as const;

export const RightNowLine: FC<SourcePreviewArgs> = (args) => {
  const { data, isError, isFetching } = useSourcePreview(args);
  if (args.containerId === null || args.source.kind === 'drops' || args.rule.advanced) return null;
  return (
    <div className={cn('flex flex-col gap-1 border-t border-border/50 pt-2 text-[11px] text-muted-foreground duration-300 animate-in fade-in-0 motion-reduce:animate-none', isFetching && 'opacity-70')} data-testid="right-now">
      <span className="flex items-center gap-1.5 font-semibold uppercase tracking-wider">
        <Activity className="h-3 w-3" aria-hidden /> Right now
        {data ? <span className="font-normal normal-case tracking-normal">· {data.rangeLabel}</span> : null}
      </span>
      {isError ? <span className="text-warning">the live read failed — the pass will judge anyway</span> : null}
      {data?.unreadable ? <span className="text-warning">the tracker could not be read — judged as unknown, which never acts</span> : null}
      {data && !data.unreadable && data.items.length === 0 && data.noListYet.length === 0 ? <span>nothing to judge yet</span> : null}
      {data?.items.map((item) => (
        <ItemLine key={item.id} item={item} />
      ))}
      {data && data.noListYet.length > 0 ? <span>{data.noListYet.join(', ')}: no list of the day yet — picked when the day is laid out</span> : null}
    </div>
  );
};

const ItemLine: FC<{ item: DropGenSourcePreviewItem }> = ({ item }) => (
  <span className="flex flex-wrap items-center gap-x-1.5" title={item.reason} data-testid="right-now-item">
    {item.groupName ? <span>{item.groupName} →</span> : null}
    <span className="font-medium text-foreground">{item.name ?? `#${item.id}`}</span>
    {Object.entries(item.bag).map(([id, v]) => (
      <span key={id}>
        · {FLOW_METRIC_LABELS[id] ?? id} {formatMetric(id, v)}
      </span>
    ))}
    <span className={cn('font-medium', VERDICT_TONE[item.verdict])}>→ {VERDICT_WORDS[item.verdict]}</span>
  </span>
);
